/* built-in imports */
import React, { useEffect, useMemo } from 'react';

/* third-party imports */
import Typography from '@mui/material/Typography';
import { Field, FieldArray, formValueSelector, getFormSyncErrors, reduxForm } from 'redux-form';
import { Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

/* internal imports */
import SelectCountries from '../../../../form/SelectCountries';
import Stockholders from './Stockholders';
import YesInput from '../../../../form/Input';
import YesNoSelector from '../../../../form/YesNoSelector';
import validate from './kycForm.validator';
import { BOOLEAN, IS_REQUIRED } from '../../../../../common/constants';
import {
  Required,
  isBicValid,
  isMaxOneHundred,
  isValidNumberString,
  requiredValidator,
  isIdentifiantFacturation,
  isTvaIntraValid
} from '../../../../../common/Validators';
import { YesButton } from '../../../../form/Button';
import { YesCheckboxV2 } from '../../../../form/CheckboxV2';
import { initKycFields } from './kycMapper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

const KycForm = ({
  change,
  invalid,
  pristine,
  isBanque,
  soumisTVA,
  aIdentifiantFacturationElectronique,
  submitting,
  donneesKyc,
  regulateur,
  handleSubmit,
  isBonsPorteur,
  synchronousError,
  isEntrepriseCotee,
  isSameCompanyAddress,
  needPaysPersonneMorale,
  tunnel
}) => {
  useEffect(() => {
    initKycFields(donneesKyc, tunnel.entreprise.siret.substring(0, 9), change);
    // launch only at component did mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { t } = useTranslation();

  const isDisabled = useMemo(
    () =>
      invalid ||
      submitting ||
      pristine ||
      (synchronousError && Object.keys(synchronousError).length !== 0) ||
      synchronousError.nbActionnaires ||
      (synchronousError.actionnaires && synchronousError.actionnaires.length),
    [invalid, submitting, pristine, synchronousError, synchronousError.nbActionnaires, synchronousError.actionnaires]
  );

  const renderError = (errors, key) => {
    return errors?.[key] && errors?.[key] !== IS_REQUIRED ? (
      <p>
        <span className="errorMsgInput">{errors[key]}</span>
      </p>
    ) : null;
  };

  return (
    <form className="formDispositifAbondement grid-container kyc-class" onSubmit={handleSubmit}>
      <Typography variant="div" className="titreFormulaire divLabel">
        <Trans i18nKey="kyc.page.title" />
      </Typography>
      <div className="grid-x grid-margin-x grid-margin-y fields-area">
        <div className="yes-label cell medium-8">Adresse postale de l&#39;entreprise :</div>
        <div className="cell medium-4">
          <Field
            id="isSameCompanyAddress"
            name="isSameCompanyAddress"
            component={YesCheckboxV2}
            type="checkbox"
            labeltext="Identique au siège social"
          />
        </div>
        {!isSameCompanyAddress && (
          <>
            <div className="cell medium-4 text-field  ">
              <Field
                id="adressePostaleEntrepriseRue"
                name="adressePostaleEntrepriseRue"
                component={YesInput}
                labeltext={"N° et rue de l'entreprise"}
                validate={requiredValidator}
              />
            </div>
            <div className="cell medium-4 text-field  ">
              <Field
                id="adressePostaleEntrepriseCode"
                name="adressePostaleEntrepriseCode"
                component={YesInput}
                labeltext={"Code postal de l'entreprise"}
                validate={requiredValidator}
              />
            </div>
            <div className="cell medium-4 text-field  ">
              <Field
                id="adressePostaleEntrepriseVille"
                name="adressePostaleEntrepriseVille"
                component={YesInput}
                labeltext={"Ville de l'entreprise"}
                validate={requiredValidator}
              />
            </div>
          </>
        )}
      </div>
      <div className="grid-x grid-margin-x grid-margin-y fields-area">
        <div className="cell medium-6 text-field formCountries">
          <Field
            id="paysFiscalEntreprise"
            name="paysFiscalEntreprise"
            component={SelectCountries}
            labeltext={"Pays fiscal de l'entreprise"}
            validate={requiredValidator}
          />
        </div>
      </div>

      <div className="grid-x grid-margin-x grid-margin-y fields-area align-left">
        <div className="cell medium-6">
          <FormControlLabel
            control={<Switch checked={soumisTVA} onChange={(e) => change('soumisTVA', e.target.checked)} color="default" />}
            label="Entreprise soumise à la TVA ?"
          />
        </div>
        <div className="cell medium-6">
          {soumisTVA && (
            <FormControlLabel
              control={
                <Switch
                  checked={aIdentifiantFacturationElectronique}
                  onChange={(e) => change('aIdentifiantFacturationElectronique', e.target.checked)}
                  color="default"
                />
              }
              label="L'entreprise a-t-elle un identifiant de facturation électronique ?"
            />
          )}
        </div>
        {soumisTVA && (
          <div className="cell medium-6 text-field">
            <Field
              id="codeTVAIntracommunautaire"
              name="codeTVAIntracommunautaire"
              component={YesInput}
              labeltext="Numéro de TVA intracommunautaire"
              validate={isTvaIntraValid}
              normalize={(value) => value?.toUpperCase().trim()}
              tooltip={t('kyc.page.tooltip.1')}
            />
          </div>
        )}
        {soumisTVA && aIdentifiantFacturationElectronique && (
          <div className="cell medium-6 text-field">
            <Field
              id="identifiantFacturationElectronique"
              name="identifiantFacturationElectronique"
              component={YesInput}
              labeltext="Identifiant de facturation électronique"
              validate={isIdentifiantFacturation}
              normalize={(value) => value?.toUpperCase().trim()}
              tooltip={t('kyc.page.tooltip.2')}
            />
          </div>
        )}
      </div>

      <YesNoSelector label="L'entreprise est un établissement bancaire" fieldName="isBanque" fieldValue={isBanque || BOOLEAN.NO}>
        <div className="grid-x grid-margin-x grid-margin-y">
          <div className="cell medium-6 text-field  ">
            <Field
              id="codeBIC"
              name="codeBIC"
              component={YesInput}
              labeltext="Code d’identification BIC"
              validate={[Required, isBicValid]}
            />
          </div>
        </div>
      </YesNoSelector>
      <YesNoSelector
        label="Entreprise exerçant une activité relevant d’un régulateur"
        fieldName="regulateur"
        fieldValue={regulateur || BOOLEAN.NO}
      >
        <div className="grid-x grid-margin-x grid-margin-y">
          <div className="cell medium-6 text-field  ">
            <Field
              id="nomRegulateur"
              name="nomRegulateur"
              component={YesInput}
              validate={requiredValidator}
              labeltext="Nom du régulateur"
            />
          </div>
          <div className="cell medium-6 text-field  ">
            <Field
              id="referenceAgrement"
              name="referenceAgrement"
              component={YesInput}
              validate={requiredValidator}
              labeltext="Référence de son agrément"
            />
          </div>
        </div>
      </YesNoSelector>
      <YesNoSelector label="Entreprise cotée" fieldName="isEntrepriseCotee" fieldValue={isEntrepriseCotee || BOOLEAN.NO}>
        <div className="grid-x grid-margin-x grid-margin-y">
          <div className="cell medium-6 formCountries">
            <Field
              id="paysCotation"
              name="paysCotation"
              component={SelectCountries}
              labeltext="Pays de cotation"
              validate={Required}
            />
          </div>
          <div className="cell medium-6">
            <Field
              id="entrepriseCotee"
              name="entrepriseCotee"
              component={YesInput}
              validate={requiredValidator}
              labeltext="Marché de cotation"
            />
          </div>
        </div>
      </YesNoSelector>
      <YesNoSelector
        label="Structure composée de bons au porteur"
        fieldName="isBonsPorteur"
        fieldValue={isBonsPorteur || BOOLEAN.NO}
      >
        <div className="grid-x grid-margin-x grid-margin-y">
          <div className="cell medium-6 text-field  ">
            <Field
              id="bonsPorteur"
              name="bonsPorteur"
              component={YesInput}
              inputMode="numeric"
              numberfield={'true'}
              validate={[Required, isValidNumberString]}
              labeltext="pourcentage"
            />
          </div>
        </div>
      </YesNoSelector>
      <YesNoSelector
        label="L'actionnaire principal est une personne morale dont l'adresse fiscale n'est pas en France"
        fieldName="needPaysPersonneMorale"
        fieldValue={needPaysPersonneMorale || BOOLEAN.NO}
      >
        <div className="grid-x grid-margin-x grid-margin-y">
          <div className="cell medium-6 text-field formCountries">
            <Field
              id="paysPersonneMorale"
              name="paysPersonneMorale"
              component={SelectCountries}
              validate={requiredValidator}
              labeltext="pays de l'adresse fiscale"
              customError={synchronousError.paysPersonneMorale}
            />
          </div>
        </div>
      </YesNoSelector>

      <div className="grid-x grid-margin-x grid-margin-y fields-area">
        <div className="cell medium-6 formCountries">
          <Field id="pays" name="pays" component={SelectCountries} validate={[Required]} labeltext="Pays" />
        </div>
        <div className="cell medium-6">
          <Field
            id="chiffreAffaire"
            name="chiffreAffaire"
            component={YesInput}
            validate={[Required, isValidNumberString, isMaxOneHundred]}
            inputMode="numeric"
            numberfield={'true'}
            labeltext="% du chiffre d'affaire"
          />
        </div>
        <div className="cell medium-6 text-field  ">
          <Field
            id="principauxMarches"
            name="principauxMarches"
            component={YesInput}
            labeltext="Principaux marchés"
            validate={requiredValidator}
          />
        </div>
        <div className="cell medium-6 text-field ">
          <Field
            id="principauxPays"
            name="principauxPays"
            component={YesInput}
            labeltext="Principaux pays où les fournisseurs sont situés"
            validate={requiredValidator}
          />
        </div>
      </div>
      <FieldArray name="actionnaires" component={Stockholders} meta={{ error: synchronousError.actionnairesArrayErrors }} />
      {renderError(synchronousError, 'nbActionnaires')}
      {renderError(synchronousError, 'parts')}
      <div className="buttonCreationContainer">
        <YesButton text="Confirmer" disabled={isDisabled} />
      </div>
    </form>
  );
};

const selectorForm = formValueSelector('annexeKyc');

const mapStateToProps = (state) => ({
  tunnel: state.tunnel,
  donneesKyc: state.tunnel.dossier.donneesAnnexeKYC,
  qualite: selectorForm(state, 'qualite'),
  isBanque: selectorForm(state, 'isBanque'),
  soumisTVA: selectorForm(state, 'soumisTVA'),
  aIdentifiantFacturationElectronique: selectorForm(state, 'aIdentifiantFacturationElectronique'),
  regulateur: selectorForm(state, 'regulateur'),
  isBonsPorteur: selectorForm(state, 'isBonsPorteur'),
  isEntrepriseCotee: selectorForm(state, 'isEntrepriseCotee'),
  isSameCompanyAddress: selectorForm(state, 'isSameCompanyAddress'),
  needPaysPersonneMorale: selectorForm(state, 'needPaysPersonneMorale'),
  hasActivityUE: selectorForm(state, 'hasActivityUE'),
  hasActivityHorsUE: selectorForm(state, 'hasActivityHorsUE'),
  synchronousError: getFormSyncErrors('annexeKyc')(state)
});

export default connect(mapStateToProps)(reduxForm({ form: 'annexeKyc', validate })(KycForm));
